import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ArrowLeft, RefreshCw, Send, Search, AlertCircle, Mail } from "lucide-react";
import WeKitLogo from "@/components/WeKitLogo";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

type LogRow = {
  id: string;
  message_id: string | null;
  template_name: string;
  recipient_email: string;
  status: string;
  error_message: string | null;
  metadata: Record<string, unknown> | null;
  created_at: string;
};

const STATUS_TONE: Record<string, string> = {
  sent: "bg-primary/10 text-primary border-primary/30",
  pending: "bg-muted text-muted-foreground border-border",
  failed: "bg-destructive/10 text-destructive border-destructive/30",
  dlq: "bg-destructive/10 text-destructive border-destructive/30",
  suppressed: "bg-amber-500/10 text-amber-600 border-amber-500/30",
  bounced: "bg-amber-500/10 text-amber-600 border-amber-500/30",
  complained: "bg-amber-500/10 text-amber-600 border-amber-500/30",
};

const STATUSES = ["all", "pending", "sent", "failed", "dlq", "suppressed", "bounced", "complained"];

/**
 * Email delivery log. Shows what went out, what didn't, and why — and lets an
 * admin re-send a failed receipt without touching the database.
 */
export default function AdminEmailLog() {
  const [rows, setRows] = useState<LogRow[]>([]);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("all");
  const [template, setTemplate] = useState("all");
  const [recipient, setRecipient] = useState("");
  const [search, setSearch] = useState("");
  const [retrying, setRetrying] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase.functions.invoke("admin-email-log", {
      body: {
        action: "list",
        status: status === "all" ? undefined : status,
        template: template === "all" ? undefined : template,
        recipient: search || undefined,
        limit: 200,
      },
    });
    setLoading(false);
    if (error || (data as { error?: string })?.error) {
      toast.error((data as { error?: string })?.error ?? "Couldn't load the email log");
      return;
    }
    const payload = data as { rows: LogRow[]; counts: Record<string, number> };
    setRows(payload.rows ?? []);
    setCounts(payload.counts ?? {});
  }, [status, template, search]);

  useEffect(() => {
    load();
  }, [load]);

  const templateOptions = useMemo(() => {
    const set = new Set(rows.map((r) => r.template_name));
    return ["all", ...Array.from(set).sort()];
  }, [rows]);

  const retry = async (row: LogRow) => {
    if (!row.message_id) {
      toast.error("This entry has no message reference to retry.");
      return;
    }
    setRetrying(row.message_id);
    const { data, error } = await supabase.functions.invoke("admin-email-log", {
      body: { action: "retry", message_id: row.message_id },
    });
    setRetrying(null);
    const err = error?.message ?? (data as { error?: string })?.error;
    if (err) {
      toast.error(err);
      return;
    }
    toast.success(`Queued another attempt to ${row.recipient_email}`);
    load();
  };

  const drain = async () => {
    const { data, error } = await supabase.functions.invoke("admin-email-log", {
      body: { action: "drain" },
    });
    const err = error?.message ?? (data as { error?: string })?.error;
    if (err) {
      toast.error(err);
      return;
    }
    toast.success("Dispatcher kicked — queued email is going out now");
    setTimeout(load, 1500);
  };

  const failing = (counts.failed ?? 0) + (counts.dlq ?? 0);

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border">
        <div className="container mx-auto flex items-center gap-3 px-4 py-4">
          <Button variant="ghost" size="icon" asChild aria-label="Back to Admin Hub">
            <Link to="/admin"><ArrowLeft className="h-5 w-5" /></Link>
          </Button>
          <WeKitLogo size="sm" clickable={false} />
          <h1 className="font-display text-xl font-semibold">Email delivery log</h1>
          <div className="ml-auto flex gap-2">
            <Button variant="outline" size="sm" onClick={drain}>
              <Send className="mr-2 h-4 w-4" aria-hidden="true" /> Send queued now
            </Button>
            <Button variant="outline" size="sm" onClick={load} disabled={loading}>
              <RefreshCw className={`mr-2 h-4 w-4 ${loading ? "animate-spin" : ""}`} aria-hidden="true" />
              Refresh
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto max-w-6xl space-y-6 px-4 py-8">
        <div className="grid gap-3 sm:grid-cols-4">
          {[
            { label: "Sent (7 days)", value: counts.sent ?? 0 },
            { label: "Waiting to send", value: counts.pending ?? 0 },
            { label: "Failed or stuck", value: failing },
            { label: "Blocked / bounced", value: (counts.suppressed ?? 0) + (counts.bounced ?? 0) },
          ].map((s) => (
            <Card key={s.label}>
              <CardContent className="p-5">
                <p className="text-xs text-muted-foreground">{s.label}</p>
                <p className="font-display text-2xl font-semibold tabular-nums">{s.value}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {failing > 0 && (
          <div className="flex items-start gap-3 rounded-2xl border border-destructive/30 bg-destructive/5 p-4">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" aria-hidden="true" />
            <p className="text-sm text-muted-foreground">
              {failing} message{failing === 1 ? "" : "s"} didn't reach their inbox in the last 7 days.
              Open the row, read the reason, then use <span className="text-foreground font-medium">Retry</span> to
              send it again with the original content.
            </p>
          </div>
        )}

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Mail className="h-4 w-4 text-primary" aria-hidden="true" /> Recent messages
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-wrap gap-2">
              <div className="relative min-w-[220px] flex-1">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
                <Input
                  value={recipient}
                  onChange={(e) => setRecipient(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && setSearch(recipient.trim())}
                  placeholder="Search by email address"
                  aria-label="Search by email address"
                  className="pl-9"
                />
              </div>
              <Button variant="secondary" onClick={() => setSearch(recipient.trim())}>Search</Button>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger className="w-[170px]" aria-label="Filter by status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUSES.map((s) => (
                    <SelectItem key={s} value={s}>{s === "all" ? "All statuses" : s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={template} onValueChange={setTemplate}>
                <SelectTrigger className="w-[220px]" aria-label="Filter by template">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {templateOptions.map((t) => (
                    <SelectItem key={t} value={t}>{t === "all" ? "All email types" : t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>When</TableHead>
                    <TableHead>Recipient</TableHead>
                    <TableHead>Email type</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Reason</TableHead>
                    <TableHead className="text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.length === 0 && !loading && (
                    <TableRow>
                      <TableCell colSpan={6} className="py-10 text-center text-sm text-muted-foreground">
                        Nothing to show for these filters.
                      </TableCell>
                    </TableRow>
                  )}
                  {rows.map((r) => (
                    <TableRow key={r.id}>
                      <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                        {new Date(r.created_at).toLocaleString()}
                      </TableCell>
                      <TableCell className="max-w-[220px] truncate text-sm">{r.recipient_email}</TableCell>
                      <TableCell className="text-xs">{r.template_name}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={STATUS_TONE[r.status] ?? ""}>{r.status}</Badge>
                      </TableCell>
                      <TableCell className="max-w-[280px] text-xs text-muted-foreground">
                        {r.error_message ?? "—"}
                      </TableCell>
                      <TableCell className="text-right">
                        {["failed", "dlq"].includes(r.status) ? (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => retry(r)}
                            disabled={retrying === r.message_id}
                          >
                            {retrying === r.message_id ? (
                              <RefreshCw className="h-4 w-4 animate-spin" aria-hidden="true" />
                            ) : (
                              "Retry"
                            )}
                          </Button>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
